import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentChoice = localStorage.getItem('cookieConsent');
    if (!consentChoice) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
    setIsVisible(false);
    
    // Enable analytics/tracking cookies
    enableAnalytics();
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookieConsent', 'essential');
    localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
    setIsVisible(false);
  };

  const handleLearnMore = () => {
    window.location.href = '/privacy-policy';
  };

  const enableAnalytics = () => {
    // TODO: Initialize analytics tools (Google Analytics, etc.)
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end pointer-events-none">
      {/* Backdrop - subtle, semi-transparent */}
      <div 
        className="absolute inset-0 bg-black/20 pointer-events-auto"
        onClick={() => setIsVisible(false)}
      />
      
      {/* Banner */}
      <div className="relative w-full max-w-2xl mx-auto mb-4 pointer-events-auto">
        <div className="mx-4 p-4 sm:p-6 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 backdrop-blur-lg">
          
          {/* Close Button */}
          <button
            onClick={() => setIsVisible(false)}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="pr-8 sm:pr-0">
            
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="w-5 h-5 flex-shrink-0 text-purple-600 dark:text-purple-400 mt-0.5" />
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Cookie Preferences
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  We use cookies to enhance your experience and analyze site usage. 
                  <button
                    onClick={handleLearnMore}
                    className="ml-1 text-purple-600 dark:text-purple-400 hover:underline font-medium"
                  >
                    Learn more
                  </button>
                </p>
              </div>
            </div>

            {/* Cookie Types Info */}
            <div className="space-y-2 mb-5 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <span className="font-medium">Essential:</span> Always active to keep the site working
              </p>
              <p>
                <span className="font-medium">Analytics:</span> Help us understand how you use our site
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectAll}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 border border-gray-300 dark:border-gray-600"
              >
                Reject
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Accept All
              </button>
            </div>

            {/* Footer Text */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Your choice is saved for 365 days. You can change it anytime in settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;

